import PDFDocument from 'pdfkit';
import BlobStream from 'blob-stream';
import { SkillData } from '../data/skills';
import { EmploymentData } from '../data/experience';
import { EducationData } from '../data/education';
import Gotham from 'url:../fonts/Gotham-Bold.otf';

export interface ContactInfo {
    address: string;
    phoneNumber: string;
    email: string;
}

const sectionOffsetY = 10;
const experienceIndent = 5;
const marginLength = 36;
const footerMargin = 10;

const fonts = {
    nameTitle: {
        name: 'NameTitle',
        size: 20,
    },
    main: {
        name: 'Main',
        size: 12,
    },
    mainBold: {
        name: 'MainBold',
        size: 12,
    },
    mainItalic: {
        name: 'MainItalic',
        size: 12,
    },
    section: {
        name: 'Section',
        size: 14,
    },
    footer: {
        name: 'MainItalic',
        size: 8,
    },
};

const colors = {
    sectionBreaks: '#000000',
    employmentBreaks: '#000000',
};

class ResumeCreator {
    private _doc: PDFKit.PDFDocument;
    private _currentFontName: string;
    private _currentFontSize: number;
    private _previousFontName: string;
    private _previousFontSize: number;

    private _nextSectionStartY: number;

    constructor(doc: PDFKit.PDFDocument) {
        this._doc = doc;
        this._currentFontName = '';
        this._currentFontSize = 0;
        this._previousFontName = '';
        this._previousFontSize = 0;
        this._nextSectionStartY = 0;
    }

    public writeContent(
        skills: SkillData[],
        employment: EmploymentData[],
        education: EducationData[],
        info: ContactInfo
    ): void {
        this._doc.on('pageAdded', () => {
            this._doc.page.margins = {
                top: marginLength,
                bottom: marginLength,
                left: marginLength,
                right: marginLength,
            };
        });
        this._doc.addPage({ margin: marginLength });

        this.setFont(fonts.main.name, fonts.main.size);
        this.writeHeader(info);
        this.writeSkills(skills);
        this.writeEmployment(employment);
        this.writeEducation(education);
        this.writeFooter();
    }

    private writeFooter(): void {
        const range = this._doc.bufferedPageRange();

        for (let i = 0; i < range.count; i++) {
            this._doc.switchToPage(i);
            this._doc.page.margins.bottom = 0;
            this._doc.page.margins.right = footerMargin;
            this.setFont(fonts.footer.name, fonts.footer.size);
            const originalPosition = {
                x: this._doc.x,
                y: this._doc.y,
            };

            const footerMessage = `Generated from ${window.location.protocol}//${window.location.hostname}/#/resume`;
            const footerDimensions = {
                w: this._doc.widthOfString(footerMessage),
                h: this._doc.heightOfString(footerMessage),
            };
            this._doc.text(
                footerMessage,
                originalPosition.x,
                this._doc.page.height - footerDimensions.h - footerMargin,
                {
                    align: 'right',
                }
            );

            this._doc.text('', originalPosition.x, originalPosition.y);
            this.setFont(this._previousFontName || fonts.main.name, this._previousFontSize || fonts.main.size);
            this._doc.page.margins.bottom = marginLength;
            this._doc.page.margins.right = marginLength;
        }
    }

    private writeHeader(info: ContactInfo): void {
        const myName = 'Matthew Swartzendruber';
        this.setFont(fonts.nameTitle.name, fonts.nameTitle.size);
        this._doc.text(myName);
        const nameDimensions = {
            width: this._doc.widthOfString(myName),
            height: this._doc.heightOfString(myName),
        };

        const myTitle = 'Senior Software Engineer';
        this.setFont(fonts.mainItalic.name, fonts.mainItalic.size);
        this._doc.text(myTitle, { align: 'right', width: nameDimensions.width });
        const titleDimensions = {
            width: this._doc.widthOfString(myTitle),
            height: this._doc.heightOfString(myTitle),
        };

        const lineOffset = 10;
        const startX = nameDimensions.width + this._doc.page.margins.left + lineOffset;
        const startY = this._doc.page.margins.top;
        const lineEndY = startY + nameDimensions.height + titleDimensions.height;
        this._doc.moveTo(startX, startY).lineTo(startX, lineEndY).stroke();

        this._nextSectionStartY = lineEndY + sectionOffsetY;

        this.setFont(fonts.main.name, fonts.main.size);
        const contactInfoStartX = startX + lineOffset;
        const contactInfoHeight = this._doc.heightOfString(info.address);
        const contactInfoStartY = this._doc.page.margins.top + nameDimensions.height / 4;
        const headerWidth =
            this._doc.page.width -
            nameDimensions.width -
            lineOffset * 2 -
            this._doc.page.margins.right -
            this._doc.page.margins.left;
        this._doc.text(info.address, contactInfoStartX, contactInfoStartY, {
            width: headerWidth,
            align: 'justify',
        });

        this._doc.text(info.phoneNumber, contactInfoStartX, contactInfoStartY + contactInfoHeight, {
            width: headerWidth,
            align: 'left',
        });

        this._doc.text(info.email, contactInfoStartX, contactInfoStartY + contactInfoHeight, {
            width: headerWidth - 10,
            align: 'right',
        });
    }

    private writeSectionName(text: string): void {
        const paddingOffset = 5 + fonts.section.size * 0.1;
        this._doc
            .moveTo(this._doc.x, this._doc.y - paddingOffset)
            .lineTo(this._doc.page.width - this._doc.page.margins.right, this._doc.y - paddingOffset)
            .strokeColor(colors.sectionBreaks)
            .lineWidth(2)
            .stroke();

        this.setFont(fonts.section.name, fonts.section.size);
        this._doc.text(` ${text}`);
        this._doc
            .moveTo(this._doc.x, this._doc.y)
            .lineTo(this._doc.page.width - this._doc.page.margins.right, this._doc.y)
            .strokeColor(colors.sectionBreaks)
            .lineWidth(2)
            .stroke();
        this.setFont(this._previousFontName, this._previousFontSize);
        this._doc.moveDown(1);
    }

    private writeSkills(skills: SkillData[]): void {
        this._doc.text('', this._doc.page.margins.left, this._nextSectionStartY);
        this.writeSectionName('Summary of Skills');

        const skillSections = skills.reduce((sections: { [key: string]: SkillData[] }, skill: SkillData) => {
            if (!skill.type) {
                return sections;
            }

            if (!sections[skill.type]) {
                sections[skill.type] = [];
            }

            sections[skill.type].push(skill);
            return sections;
        }, {});

        Object.entries(skillSections).forEach(([section, skills]) => {
            this.setFont(fonts.mainBold.name, fonts.mainBold.size);
            this._doc.text(`${section}: `, {
                continued: true,
            });
            this.setFont(fonts.main.name, fonts.main.size);
            skills.forEach((skill, index) => {
                const lastEntry = index == skills.length - 1;
                let output = `${skill.name}, `;
                if (lastEntry) {
                    output = output.slice(0, -2);
                }
                this._doc.text(output, {
                    continued: !lastEntry,
                    lineGap: 5,
                });
            });

            this._nextSectionStartY = this._doc.y + sectionOffsetY;
        });
    }

    private writeEmployment(employments: EmploymentData[]): void {
        this._doc.text('', this._doc.page.margins.left, this._nextSectionStartY);
        this.writeSectionName('Professional Experience');
        const startX = this._doc.x;

        employments.forEach(employment => {
            this.setFont(fonts.mainBold.name, fonts.mainBold.size);
            this._doc.text(employment.companyName, startX, this._doc.y, {
                continued: true,
                align: 'left',
            });
            this.setFont(fonts.main.name, fonts.main.size);
            this._doc.text(employment.location, {
                continued: true,
                align: 'center',
            });
            this._doc.text(employment.employmentDate, {
                continued: false,
                align: 'right',
            });
            this._doc
                .moveTo(this._doc.x, this._doc.y)
                .lineTo(this._doc.page.width - this._doc.page.margins.right, this._doc.y)
                .strokeColor(colors.employmentBreaks)
                .lineWidth(1)
                .stroke();

            this._doc.moveDown(0.5);

            this._doc.text(employment.description, this._doc.x + experienceIndent, this._doc.y, {
                align: 'justify',
                width:
                    this._doc.page.width -
                    this._doc.page.margins.left -
                    this._doc.page.margins.right -
                    experienceIndent * 2,
            });

            this._doc.moveDown(0.5);

            const titleStartX = this._doc.x;
            employment.employmentTitles.forEach(title => {
                this.setFont(fonts.mainItalic.name, fonts.mainItalic.size);
                this._doc.moveDown(0.5);
                this._doc.text(title.title, titleStartX + experienceIndent * 2, this._doc.y, {
                    align: 'left',
                    continued: true,
                    width:
                        this._doc.page.width -
                        this._doc.page.margins.left -
                        this._doc.page.margins.right -
                        experienceIndent * 4,
                });
                this._doc.text(title.titleDate, {
                    align: 'right',
                    continued: false,
                });

                this.setFont(fonts.main.name, fonts.main.size);
                this._doc.moveDown(1);
                this._doc.list(title.summaryPoints, {
                    lineGap: 2,
                    width:
                        this._doc.page.width -
                        this._doc.page.margins.left -
                        this._doc.page.margins.right -
                        experienceIndent * 4,
                });
                this._doc.moveDown(0.5);
            });

            this._doc.moveDown(1);
        });

        this._nextSectionStartY = this._doc.y + sectionOffsetY;
    }

    private writeEducation(education: EducationData[]): void {
        this._doc.text('', this._doc.page.margins.left, this._nextSectionStartY);
        this.writeSectionName('Education');

        const startX = this._doc.x;

        education.forEach(item => {
            this.setFont(fonts.mainBold.name, fonts.mainBold.size);
            this._doc.text(item.establishmentName, startX, this._doc.y, {
                continued: true,
                align: 'left',
            });
            this.setFont(fonts.main.name, fonts.main.size);
            this._doc.text(item.location, {
                continued: true,
                align: 'center',
            });
            this._doc.text(item.attendedDate, {
                continued: false,
                align: 'right',
            });
            this._doc
                .moveTo(this._doc.x, this._doc.y)
                .lineTo(this._doc.page.width - this._doc.page.margins.right, this._doc.y)
                .strokeColor(colors.employmentBreaks)
                .lineWidth(1)
                .stroke();

            this._doc.moveDown(0.5);

            this._doc.text(item.accomplishment, this._doc.x + experienceIndent, this._doc.y, {
                align: 'justify',
                width:
                    this._doc.page.width -
                    this._doc.page.margins.left -
                    this._doc.page.margins.right -
                    experienceIndent * 2,
            });
            this._doc.moveDown(1);
        });
    }

    private setFont(fontName: string, fontSize: number): void {
        this._previousFontName = this._currentFontName;
        this._previousFontSize = this._currentFontSize;
        this._doc.font(fontName).fontSize(fontSize);
        this._currentFontName = fontName;
        this._currentFontSize = fontSize;
    }
}

const registerFonts = async (doc: PDFKit.PDFDocument): Promise<void> => {
    const getBlob = async (fontPath: string) => await (await fetch(fontPath)).arrayBuffer();
    doc.registerFont(fonts.nameTitle.name, await getBlob(Gotham));
    doc.registerFont(fonts.main.name, 'Times-Roman');
    doc.registerFont(fonts.mainBold.name, 'Times-Bold');
    doc.registerFont(fonts.mainItalic.name, 'Times-Italic');
    doc.registerFont(fonts.section.name, 'Times-BoldItalic');
};

export const createPDF = async (
    skills: SkillData[],
    employment: EmploymentData[],
    education: EducationData[],
    contactInfo: ContactInfo
): Promise<string> => {
    const doc = new PDFDocument({ autoFirstPage: false, bufferPages: true });
    const pdfStream = doc.pipe(BlobStream());
    await registerFonts(doc);

    const creator = new ResumeCreator(doc);
    creator.writeContent(skills, employment, education, contactInfo);

    doc.end();

    return await new Promise(resolve => {
        pdfStream.on('finish', () => {
            const url = pdfStream.toBlobURL('application/pdf');
            resolve(url);
        });
    });
};
